import React, { useState, useRef, useEffect, useCallback } from 'react';
import ActivityBar from './ActivityBar';
import SidePanel from './SidePanel';
import TabBar from './TabBar';
import EditorArea from './EditorArea';
import StatusBar from './StatusBar';
import { InstallBanner } from '../common/InstallApp';
import { getProjects } from '../../api/projects';
import { updateMyProfile } from '../../api/users';
import { refreshTokenIfStale } from '../../api/auth';
import { getGroups } from '../../api/chat';
import { getUnreadCount } from '../../api/jobPackages';
import { getFeedbackCount } from '../../api/feedback';
import { initSocket, disconnectSocket } from '../../socket';
import { registerPush } from '../../utils/push';
import { isTabDirty, setTabDirty } from '../../utils/unsavedTabs';
import './AppShell.css';

const PROJECT_TABS = new Set([
  'load-list', 'cable-register', 'gantt', 'job-packages', 'project-calendar',
  'switchboards', 'rooms', 'systems', 'sections', 'cable-types', 'emg-groups',
  'fuse-list', 'megger-lists', 'load-calc', 'sld', 'quote-summary',
]);

function AppShell({ user, role, onLogout, onUserUpdate }) {
  const [activeSection, setActiveSection]   = useState('ships');
  const [sidePanelOpen, setSidePanelOpen]   = useState(true);
  const [mobileShowPanel, setMobileShowPanel] = useState(false);
  const [tabs, setTabs]       = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [previewTabId, setPreviewTabId] = useState(null);
  const previewRef = useRef(null);

  const [projects, setProjects]           = useState([]);
  const [activeProject, setActiveProject] = useState(null);

  const [socket, setSocket]         = useState(null);
  const [chatGroups, setChatGroups] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [jobPkgUnread, setJobPkgUnread]   = useState(0);
  const [feedbackCount, setFeedbackCount] = useState(0);

  // Refs to avoid stale closures in socket handlers
  const chatGroupsRef    = useRef([]);
  const activeChatRef    = useRef(null);
  const userIdRef        = useRef(user?.id);

  useEffect(() => { chatGroupsRef.current = chatGroups; }, [chatGroups]);
  useEffect(() => { userIdRef.current = user?.id; }, [user?.id]);

  // Deep-link navigation from push notification taps
  const pendingNavRef = useRef(null);
  const openTabRef    = useRef(null);   // kept fresh every render (see below)

  // Cold-start: read intent encoded in ?sw= URL param
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const encoded = p.get('sw');
    if (!encoded) return;
    try { pendingNavRef.current = JSON.parse(atob(encoded)); } catch {}
    window.history.replaceState({}, '', '/');
  }, []);

  // Warm-start: service worker postMessage while app is open
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (e) => {
      if (e.data?.type === 'sw:open') pendingNavRef.current = e.data;
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    getProjects().then(res => {
      setProjects(res.data);
      // Prefer server-stored lastProject (cross-device), fall back to localStorage
      const preferredId = user?.lastProject || localStorage.getItem('kspro_project');
      if (preferredId) {
        const found = res.data.find(p => p._id === preferredId);
        if (found) setActiveProject(found);
      }
    }).catch(() => {});
  }, []);

  // The chat being looked at right now — its messages are read as they arrive
  const isChatVisible = (groupId) =>
    groupId === activeChatRef.current && document.visibilityState === 'visible';

  // Server counts are the source of truth; the visible chat is being marked read by ChatWindow
  const loadChatGroups = useCallback(() => {
    if (role === 'client') return; // clients have no chat
    getGroups()
      .then(r => setChatGroups(r.data.map(g => isChatVisible(g._id) ? { ...g, unread: 0 } : g)))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Socket.io + push notifications
  useEffect(() => {
    const token = localStorage.getItem('kspro_token');
    if (!token) return;
    const s = initSocket(token);
    setSocket(s);
    registerPush().catch(() => {});
    refreshTokenIfStale().catch(() => {});
    return () => disconnectSocket();
  }, []);

  // Load groups + job package unread count on mount
  useEffect(() => {
    loadChatGroups();
    if (role !== 'client') getUnreadCount().then(r => setJobPkgUnread(r.data.count)).catch(() => {});
    if (role === 'admin') getFeedbackCount().then(r => setFeedbackCount(r.data.count)).catch(() => {});
  }, [loadChatGroups]);

  // Always-on socket listener — updates badge from any section
  useEffect(() => {
    if (!socket) return;

    const onNewMessage = ({ message }) => {
      if (isChatVisible(message.group)) return;
      if ((message.sender?._id || message.sender) === userIdRef.current) return;
      // Unknown group (e.g. someone just opened a DM with us) — reload to pick it up with its count
      if (!chatGroupsRef.current.some(g => g._id === message.group)) {
        loadChatGroups();
        return;
      }
      setChatGroups(prev => prev.map(g =>
        g._id === message.group ? { ...g, unread: (g.unread || 0) + 1 } : g
      ));
    };

    const onGroupNew     = ({ group }) => setChatGroups(prev =>
      prev.find(g => g._id === group._id) ? prev : [...prev, { ...group, unread: 0 }]
    );
    const onGroupDeleted = ({ groupId }) => setChatGroups(prev => prev.filter(g => g._id !== groupId));
    const onGroupLeft    = ({ groupId }) => setChatGroups(prev => prev.filter(g => g._id !== groupId));
    const onGroupUpdated = ({ group }) => setChatGroups(prev =>
      prev.map(g => g._id === group._id ? { ...g, ...group } : g)
    );

    // Read on another device/tab
    const onChatRead = ({ groupId }) => setChatGroups(prev =>
      prev.map(g => g._id === groupId && g.unread ? { ...g, unread: 0 } : g)
    );

    // Messages sent while disconnected (sleep, mobile background, server restart) never
    // arrive as events — resync counts from the server on reconnect and when the app returns
    const onReconnect = () => loadChatGroups();
    let hiddenAt = null;
    const onVisible   = () => {
      if (document.visibilityState !== 'visible') { hiddenAt = Date.now(); return; }
      refreshTokenIfStale().catch(() => {});
      loadChatGroups();
      // Phones suspend the app in the background and often leave a dead connection that
      // still reports connected for up to ~45s — start a fresh one after a real absence.
      // Also covers socket.io giving up after the server rejected the handshake.
      if (!socket.connected || (hiddenAt && Date.now() - hiddenAt > 10000)) {
        socket.disconnect().connect();
      }
      hiddenAt = null;
    };
    // Safety net while the socket is down: keep badge counts current by polling
    const pollTimer = setInterval(() => {
      if (!socket.connected && document.visibilityState === 'visible') loadChatGroups();
    }, 30000);
    // Rejected handshake (token expired/invalid): renew and retry; a 401 logs out instead
    const onConnectError = (err) => {
      if (err.message !== 'Invalid token') return;
      refreshTokenIfStale(true).then(() => socket.connect()).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);

    const onUsersOnline  = (ids) => setOnlineUsers(ids);
    // The server broadcasts the list when we connect, but this listener may not be attached
    // yet at that moment — ask for it on every (re)connect
    const onConnect      = () => socket.emit('users:online:get');
    if (socket.connected) onConnect();
    const onJobAssigned  = ()    => setJobPkgUnread(prev => prev + 1);

    socket.on('message:new',        onNewMessage);
    socket.on('chat:read',          onChatRead);
    socket.io.on('reconnect',       onReconnect);
    socket.on('connect_error',      onConnectError);
    socket.on('group:new',          onGroupNew);
    socket.on('group:deleted',      onGroupDeleted);
    socket.on('group:left',         onGroupLeft);
    socket.on('group:updated',      onGroupUpdated);
    socket.on('users:online',       onUsersOnline);
    socket.on('connect',            onConnect);
    socket.on('jobpackage:assigned', onJobAssigned);

    return () => {
      socket.off('message:new',        onNewMessage);
      socket.off('chat:read',          onChatRead);
      socket.io.off('reconnect',       onReconnect);
      socket.off('connect_error',      onConnectError);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(pollTimer);
      socket.off('group:new',          onGroupNew);
      socket.off('group:deleted',      onGroupDeleted);
      socket.off('group:left',         onGroupLeft);
      socket.off('group:updated',      onGroupUpdated);
      socket.off('users:online',       onUsersOnline);
      socket.off('connect',            onConnect);
      socket.off('jobpackage:assigned', onJobAssigned);
    };
  }, [socket, loadChatGroups]);

  const totalChatUnread = chatGroups.reduce((s, g) => s + (g.unread || 0), 0);

  // Update the PWA home screen icon badge count
  useEffect(() => {
    if (!('setAppBadge' in navigator)) return;
    if (totalChatUnread > 0) {
      navigator.setAppBadge(totalChatUnread).catch(() => {});
    } else {
      navigator.clearAppBadge().catch(() => {});
    }
  }, [totalChatUnread]);

  const _setPreview = (id) => { previewRef.current = id; setPreviewTabId(id); };

  const openTab = (id, label, extra = {}) => {
    const cur = previewRef.current;
    // PDFs always get their own sticky tab and never evict the current preview tab
    const isPdf = extra.type === 'pdf-viewer' || extra.type === 'print-pdf' || id.startsWith('print-pdf-');
    setTabs(prev => {
      let list = (!isPdf && cur && cur !== id) ? prev.filter(t => t.id !== cur) : prev;
      if (list.find(t => t.id === id)) {
        return list.map(t => t.id === id ? { ...t, preview: false, ...extra } : t);
      }
      return [...list, { id, label, preview: false, ...extra }];
    });
    if (!isPdf && cur) _setPreview(null);
    setActiveTab(id);
    setMobileShowPanel(false);
  };

  const openPreviewTab = (id, label, extra = {}) => {
    const cur = previewRef.current;
    setTabs(prev => {
      let list = (cur && cur !== id) ? prev.filter(t => t.id !== cur) : prev;
      if (list.find(t => t.id === id)) return list;
      return [...list, { id, label, preview: true, ...extra }];
    });
    _setPreview(id);
    setActiveTab(id);
    setMobileShowPanel(false);
  };

  const closeTab = (tabId) => {
    if (isTabDirty(tabId) && !confirm('This tab has unsaved changes. Close it anyway?')) return;
    setTabDirty(tabId, false);
    if (previewRef.current === tabId) _setPreview(null);
    setTabs(prev => {
      const next = prev.filter(t => t.id !== tabId);
      if (activeTab === tabId) setActiveTab(next.length ? next[next.length - 1].id : null);
      return next;
    });
  };

  const selectTab = (tabId) => {
    setActiveTab(tabId);
    const tab = tabs.find(t => t.id === tabId);
    if (tab?.project) setActiveProject(tab.project);
  };

  const handleSetProject = (project) => {
    setActiveProject(project);
    localStorage.setItem('kspro_project', project._id);
    updateMyProfile({ lastProject: project._id }).catch(() => {});
  };

  const handleOpenProjectInfo = (project) => {
    handleSetProject(project);
    openTab(`project-info-${project._id}`, project.name, { type: 'project-info', project });
  };

  const handleProjectSaved = (project) => {
    setProjects(prev => {
      const idx = prev.findIndex(p => p._id === project._id);
      return idx >= 0 ? prev.map(p => p._id === project._id ? project : p) : [...prev, project];
    });
    setTabs(prev => prev.map(t => t.project?._id === project._id ? { ...t, project } : t));
    setActiveProject(project);
    localStorage.setItem('kspro_project', project._id);
    updateMyProfile({ lastProject: project._id }).catch(() => {});
  };

  // Keep activeChatRef in sync for socket handler
  const activeChatGroupId = (() => {
    const tab = tabs.find(t => t.id === activeTab);
    return tab?.type === 'chat-window' ? tab.group?._id : null;
  })();
  activeChatRef.current = activeChatGroupId;

  // Switching to an already-open chat tab clears its unread count
  useEffect(() => {
    if (!activeChatGroupId) return;
    setChatGroups(prev => prev.map(g => g._id === activeChatGroupId && g.unread ? { ...g, unread: 0 } : g));
  }, [activeChatGroupId]);

  const handleOpenChat = (group, resolvedName) => {
    const id    = `chat-${group._id}`;
    const label = group.type === 'dm'
      ? `DM: ${resolvedName || 'User'}`
      : (group.name || 'Chat');
    openTab(id, label, { type: 'chat-window', group });
    setChatGroups(prev => prev.map(g => g._id === group._id ? { ...g, unread: 0 } : g));
    setActiveSection('chat');
  };

  // Keep openTabRef current so pending-nav effects always call the fresh version
  openTabRef.current = openTab;

  // Process pending nav when chat groups load/change
  useEffect(() => {
    const nav = pendingNavRef.current;
    if (!nav || nav.kind !== 'chat') return;
    const group = chatGroups.find(g => g._id === nav.groupId);
    if (!group) return;
    pendingNavRef.current = null;
    const label = group.type === 'dm' ? `DM: ${nav.senderName || 'User'}` : (group.name || 'Chat');
    openTabRef.current(`chat-${group._id}`, label, { type: 'chat-window', group });
    setChatGroups(prev => prev.map(g => g._id === nav.groupId ? { ...g, unread: 0 } : g));
    setActiveSection('chat');
  }, [chatGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  // Process pending nav when projects load/change
  useEffect(() => {
    const nav = pendingNavRef.current;
    if (!nav || nav.kind !== 'pkg') return;
    pendingNavRef.current = null;
    const PRIV = ['admin','engineer','manager','foreman'];
    if (PRIV.includes(role) && nav.projectId) {
      const project = projects.find(p => p._id === nav.projectId);
      if (project) {
        openTabRef.current(`job-packages-${project._id}`, 'Job Packages', { type: 'job-packages', project, highlightPkg: nav.pkgNum });
        setActiveSection('job-packages');
        return;
      }
    }
    openTabRef.current('my-job-packages', 'My Job Packages', { type: 'my-job-packages', highlightPkg: nav.pkgNum });
    setActiveSection('job-packages');
  }, [projects, role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ctrl+½ — cycle through open tabs in MRU order (like VS Code Ctrl+Tab)
  const tabsRef        = useRef(tabs);
  const activeTabRef   = useRef(activeTab);
  const tabHistoryRef  = useRef([]);    // tab IDs, most-recently-used first
  const ctrlCyclingRef = useRef(false); // true while Ctrl is held mid-cycle
  const ctrlIdxRef     = useRef(0);     // position in history during cycle
  const ctrlCommitRef  = useRef(null);  // tab selected during cycling

  useEffect(() => { tabsRef.current = tabs; }, [tabs]);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  // Maintain MRU history on tab switch (skip while Ctrl-cycling)
  useEffect(() => {
    if (ctrlCyclingRef.current || !activeTab) return;
    tabHistoryRef.current = [activeTab, ...tabHistoryRef.current.filter(id => id !== activeTab)];
  }, [activeTab]);

  // Prune closed tabs from history
  useEffect(() => {
    const ids = new Set(tabs.map(t => t.id));
    tabHistoryRef.current = tabHistoryRef.current.filter(id => ids.has(id));
  }, [tabs]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!e.ctrlKey || e.code !== 'Backquote') return;
      const ts = tabsRef.current;
      if (ts.length < 2) return;
      e.preventDefault();
      const history = tabHistoryRef.current.filter(id => ts.find(t => t.id === id));
      if (history.length < 2) return;
      if (!ctrlCyclingRef.current) {
        ctrlCyclingRef.current = true;
        ctrlIdxRef.current = 1;
      } else {
        ctrlIdxRef.current++;
      }
      const nextId = history[ctrlIdxRef.current % history.length];
      ctrlCommitRef.current = nextId;
      setActiveTab(nextId);
      const tab = ts.find(t => t.id === nextId);
      if (tab?.project) setActiveProject(tab.project);
    };

    const onKeyUp = (e) => {
      if (e.key !== 'Control' || !ctrlCyclingRef.current) return;
      ctrlCyclingRef.current = false;
      const committed = ctrlCommitRef.current;
      ctrlCommitRef.current = null;
      if (committed) {
        tabHistoryRef.current = [committed, ...tabHistoryRef.current.filter(id => id !== committed)];
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const shellClass = [
    'app-shell',
    !sidePanelOpen ? 'panel-closed' : '',
    mobileShowPanel ? 'mob-panel' : 'mob-editor',
  ].filter(Boolean).join(' ');

  return (
    <div className={shellClass}>
      <InstallBanner />
      <ActivityBar
        activeSection={activeSection}
        role={role}
        totalChatUnread={totalChatUnread}
        jobPkgUnread={jobPkgUnread}
        feedbackCount={feedbackCount}
        activeProject={activeProject}
        onSectionChange={(id) => {
          setActiveSection(id);
          setSidePanelOpen(true);
          setMobileShowPanel(true);
        }}
        onLogout={onLogout}
        onOpenSettings={() => openTab('settings', 'Settings', { type: 'settings' })}
      />
      <div className="side-panel-wrapper">
        <SidePanel
          activeSection={activeSection}
          activeTabType={tabs.find(t => t.id === activeTab)?.type ?? activeTab}
          activeTabId={activeTab}
          activeProject={activeProject}
          projects={projects}
          role={role}
          socket={socket}
          currentUser={user}
          chatGroups={chatGroups}
          setChatGroups={setChatGroups}
          activeChatGroupId={activeChatGroupId}
          onOpenChat={handleOpenChat}
          onlineUsers={onlineUsers}
          onOpenTab={(id, label, extra = {}) => {
            if (id.startsWith('files-folder-')) {
              openTab(id, label, { project: activeProject, ...extra });
            } else if (activeProject && PROJECT_TABS.has(id)) {
              openTab(`${id}-${activeProject._id}`, label, { type: id, project: activeProject, ...extra });
            } else {
              openTab(id, label, extra);
            }
          }}
          onPreviewTab={(id, label, extra = {}) => {
            if (id.startsWith('files-folder-')) {
              openPreviewTab(id, label, { project: activeProject, ...extra });
            } else if (activeProject && PROJECT_TABS.has(id)) {
              openPreviewTab(`${id}-${activeProject._id}`, label, { type: id, project: activeProject, ...extra });
            } else {
              openPreviewTab(id, label, extra);
            }
          }}
          onSelectProject={handleSetProject}
          onOpenProjectInfo={handleOpenProjectInfo}
          onNewProject={() => openTab('project-new', 'New Project')}
        />
      </div>
      <div className="editor-column">
        <TabBar
          tabs={tabs}
          activeTab={activeTab}
          previewTabId={previewTabId}
          onSelectTab={selectTab}
          onCloseTab={closeTab}
          onToggleSidePanel={() => { setSidePanelOpen(v => !v); setMobileShowPanel(true); }}
          sidePanelOpen={sidePanelOpen}
        />
        <EditorArea
          activeTab={activeTab}
          tabs={tabs}
          activeProject={activeProject}
          role={role}
          socket={socket}
          currentUser={user}
          projects={projects}
          onOpenTab={openTab}
          onCloseTab={closeTab}
          setTabs={setTabs}
          setActiveTab={setActiveTab}
          onProjectSaved={handleProjectSaved}
          onUserUpdate={onUserUpdate}
          onlineUsers={onlineUsers}
          onJobPkgRead={() => setJobPkgUnread(prev => Math.max(0, prev - 1))}
          onFeedbackRead={() => role === 'admin' && getFeedbackCount().then(r => setFeedbackCount(r.data.count)).catch(() => {})}
        />
      </div>
      <StatusBar user={user} role={role} activeProject={activeProject} />
    </div>
  );
}

export default AppShell;
